import { useCallback, useRef } from 'react';
import { DISCOVERY_STATUS } from './useToolDiscovery';

/**
 * Wires one discovery-driven source card (MCP / Swagger) to the shared
 * `useToolDiscovery` hook: runs discovery for the card's link, applies the
 * discovered tools to the source (all selected by default, previous
 * unchecks preserved) and skips redundant runs (same link as the last
 * discovery, or an empty link).
 */
export const useSourceDiscovery = ({ source, discovery, onChange }) => {
    const lastDiscoveredUrl = useRef(null);
    const status = discovery.statusById[source.id] || { status: DISCOVERY_STATUS.idle };

    const runDiscovery = useCallback(
        async (trigger) => {
            const url = (source.discovery_url || '').trim();
            if (!url) {
                if (source.tools.length) onChange({ tools: [], selected_tools: [] });
                discovery.clearStatus(source.id);
                lastDiscoveredUrl.current = null;
                return;
            }
            // Blur re-runs only when the link changed; Enter always re-runs.
            if (trigger === 'blur' && lastDiscoveredUrl.current === url) return;
            lastDiscoveredUrl.current = url;
            const result = await discovery.discover(source.id, source.type, url);
            if (result.stale) return;
            const previousNames = new Set(source.tools.map((t) => t.name));
            const previouslySelected = new Set(source.selected_tools);
            onChange({
                tools: result.tools,
                selected_tools: result.tools
                    .map((t) => t.name)
                    .filter((name) => !previousNames.has(name) || previouslySelected.has(name)),
            });
        },
        [discovery, onChange, source]
    );

    const toggleTool = useCallback(
        (name) => {
            const selected = new Set(source.selected_tools);
            if (selected.has(name)) selected.delete(name);
            else selected.add(name);
            onChange({
                selected_tools: source.tools.map((t) => t.name).filter((n) => selected.has(n)),
            });
        },
        [onChange, source]
    );

    /** Bulk "Select All" / "Clear All": keeps `selected_tools` in `tools` order. */
    const setSelectedTools = useCallback(
        (names) => {
            const selected = new Set(names);
            onChange({
                selected_tools: source.tools.map((t) => t.name).filter((n) => selected.has(n)),
            });
        },
        [onChange, source]
    );

    return {
        runDiscovery,
        toggleTool,
        setSelectedTools,
        loading: status.status === DISCOVERY_STATUS.loading,
        discoveryError: status.status === DISCOVERY_STATUS.error ? status.error : null,
    };
};

export default useSourceDiscovery;
